import { useEffect, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { ensureCompanyDetail } from '@/store/actions/company';
import { useApprovalContext } from '../hooks/useApprovals';
import { hasApprovalAdminAccess } from '../navigation';

export const useApprovalAdminAccess = () =>
  useSelector((state: any) => !state.menu.loadingMenu && hasApprovalAdminAccess(state.menu.menu));

export default function ApprovalAccess({
  adminRoute,
  children,
}: {
  adminRoute?: string;
  children: ReactNode;
}) {
  const dispatch = useDispatch();
  const companyId = useSelector((state: any) => state.auth.current_company);
  const { detail, loading, loadError } = useSelector((state: any) => state.company);
  const { menu, loadingMenu } = useSelector((state: any) => state.menu);
  const context = useApprovalContext();

  // Admin pages are the only place that still needs the (admin-only) company
  // detail; non-admin routes are gated by /approvals/context/ instead.
  useEffect(() => {
    if (adminRoute && companyId) dispatch(ensureCompanyDetail() as any);
  }, [adminRoute, companyId, dispatch]);

  if (!companyId) return <div className="p-6 text-sm">Nejprve vyberte firmu.</div>;

  if (adminRoute) {
    if (loadingMenu || loading) return <SpinnerOverlay tip="Načítání schvalování…" />;
    if (!hasApprovalAdminAccess(menu, adminRoute)) {
      return <div className="p-6 text-sm">Ke správě schvalování nemáte oprávnění.</div>;
    }
    if (String(detail?.id) !== String(companyId)) {
      return loadError ? (
        <div className="p-6 text-sm">Nastavení firmy se nepodařilo načíst. Obnovte stránku.</div>
      ) : (
        <SpinnerOverlay tip="Načítání firmy…" />
      );
    }
    return <>{children}</>;
  }

  if (context.isLoading) return <SpinnerOverlay tip="Načítání schvalování…" />;
  if (context.isError || !context.data) {
    return (
      <div className="p-6 text-sm">Stav schvalování se nepodařilo načíst. Obnovte stránku.</div>
    );
  }
  if (!context.data.enabled) {
    return <div className="p-6 text-sm">Vaše firma schvalovací workflow nepoužívá.</div>;
  }
  return <>{children}</>;
}
